/**
 * Floating Workflow Orchestration Menu
 *
 * Collapsible menu that can be positioned:
 * - Bottom footer (horizontal layout)
 * - Right sidebar (vertical layout)
 *
 * Features:
 * - Quick access to all 25 primitives
 * - Live status indicators
 * - Smooth animations
 * - Keyboard shortcuts (Ctrl+O to toggle)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Badge,
  ScrollArea,
  Collapse,
  Divider,
  UnstyledButton,
  Box,
  Transition,
} from '@mantine/core';
import {
  IconChevronUp,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconSitemap,
  IconX,
  IconSettings,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

// DATA-DRIVEN: Use centralized primitive metadata
import {
  getPrimitivesByPhase,
  LIFECYCLE_PHASES,
  type PrimitiveMetadata,
  type LifecyclePhase,
} from '@/config/primitive-metadata';

// OPTIONAL: If you want live counts from backend
// import { usePrimitiveData } from '@/components/UnifiedPrimitiveForm/hooks/usePrimitiveData';

type Position = 'bottom' | 'right';

interface FloatingOrchestrationMenuProps {
  position?: Position;
  initiallyOpen?: boolean;
}

export function FloatingOrchestrationMenu({
  position = 'bottom',
  initiallyOpen = false,
}: FloatingOrchestrationMenuProps) {
  const [opened, setOpened] = useState(initiallyOpen);
  const [showMenu, setShowMenu] = useState(true);
  const navigate = useNavigate();

  // DATA-DRIVEN: Get primitives from centralized config
  // OPTIONAL: Uncomment to get live counts from backend
  // const primitiveData = usePrimitiveData();

  // Organize primitives by phase for display
  const primitivesByPhase = useMemo(() => {
    const result: Record<LifecyclePhase, PrimitiveMetadata[]> = {
      DESIGN: [],
      CONFIGURE: [],
      EXECUTE: [],
      MONITOR: [],
      OPTIMIZE: [],
    };

    LIFECYCLE_PHASES.forEach((phase) => {
      result[phase] = getPrimitivesByPhase(phase);
    });

    return result;
  }, []);

  // Keyboard shortcut: Ctrl+O to toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'o') {
        e.preventDefault();
        setOpened((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!showMenu) return null;

  const isBottom = position === 'bottom';

  return (
    <Transition
      mounted={true}
      transition={isBottom ? 'slide-up' : 'slide-left'}
      duration={300}
      timingFunction="ease"
    >
      {(styles) => (
        <Paper
          shadow="xl"
          radius="md"
          style={{
            position: 'fixed',
            ...(isBottom
              ? {
                  bottom: 0,
                  left: 0,
                  right: 0,
                  margin: '0 auto',
                  maxWidth: '95%',
                  width: 'fit-content',
                  marginBottom: opened ? 0 : -10,
                }
              : {
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  marginRight: opened ? 0 : -10,
                  maxHeight: '90vh',
                }),
            zIndex: 1000,
            ...styles,
          }}
          withBorder
        >
          {isBottom ? (
            // Bottom layout (horizontal)
            <Stack gap={0}>
              {/* Header */}
              <Group
                justify="space-between"
                p="xs"
                style={{
                  background: 'var(--mantine-color-violet-1)',
                  borderBottom: opened ? '1px solid var(--mantine-color-gray-3)' : 'none',
                  borderTopLeftRadius: 'var(--mantine-radius-md)',
                  borderTopRightRadius: 'var(--mantine-radius-md)',
                  cursor: 'pointer',
                }}
                onClick={() => setOpened(!opened)}
              >
                <Group gap="xs">
                  <IconSitemap size={18} color="var(--mantine-color-violet-6)" />
                  <Text size="sm" fw={600} c="violet">
                    Workflow Orchestration
                  </Text>
                  <Badge size="xs" variant="light" c="violet">
                    25 Primitives
                  </Badge>
                  <Text size="xs" c="dimmed">
                    (Ctrl+O)
                  </Text>
                </Group>

                <Group gap={4}>
                  <Tooltip label="Settings">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/architect/primitives');
                      }}
                    >
                      <IconSettings size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Close Menu">
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      c="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(false);
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon variant="subtle" size="sm">
                    {opened ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
                  </ActionIcon>
                </Group>
              </Group>

              {/* Content */}
              <Collapse in={opened}>
                <ScrollArea type="auto" p="md" mah={300}>
                  <Group gap="md" wrap="nowrap">
                    {/* Group by phase */}
                    {LIFECYCLE_PHASES.map((phase) => (
                      <Stack key={phase} gap={6}>
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                          {phase}
                        </Text>
                        {primitivesByPhase[phase].map((primitive) => {
                          const Icon = primitive.icon;
                          // OPTIONAL: Get live count from backend
                          // const count = primitiveData.getPrimitiveByType(primitive.id).stats.total;
                          return (
                            <Tooltip key={primitive.id} label={primitive.description}>
                              <UnstyledButton
                                onClick={() => navigate(primitive.path)}
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: 'var(--mantine-radius-sm)',
                                  transition: 'background 0.1s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'var(--mantine-color-gray-1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                <Group gap={6}>
                                  <Icon size={14} color={`var(--mantine-color-${primitive.phaseColor}-6)`} />
                                  <Text size="xs" fw={500}>
                                    {primitive.name}
                                  </Text>
                                  {/* OPTIONAL: Show live count
                                  {count > 0 && (
                                    <Badge size="xs" variant="light" c={primitive.phaseColor}>
                                      {count}
                                    </Badge>
                                  )}
                                  */}
                                </Group>
                              </UnstyledButton>
                            </Tooltip>
                          );
                        })}
                      </Stack>
                    ))}
                  </Group>
                </ScrollArea>
              </Collapse>
            </Stack>
          ) : (
            // Right layout (vertical)
            <Group gap={0} wrap="nowrap">
              {/* Content */}
              {/* @ts-expect-error Mantine Collapse orientation prop */}
              <Collapse in={opened} orientation={'horizontal' as const}>
                <ScrollArea type="auto" p="md" maw={250}>
                  <Stack gap="md">
                    {LIFECYCLE_PHASES.map((phase) => (
                      <div key={phase}>
                        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={6}>
                          {phase}
                        </Text>
                        <Stack gap={4}>
                          {primitivesByPhase[phase].map((primitive) => {
                            const Icon = primitive.icon;
                            // OPTIONAL: Get live count from backend
                            // const count = primitiveData.getPrimitiveByType(primitive.id).stats.total;
                            return (
                              <UnstyledButton
                                key={primitive.id}
                                onClick={() => navigate(primitive.path)}
                                style={{
                                  padding: '6px 8px',
                                  borderRadius: 'var(--mantine-radius-sm)',
                                  transition: 'background 0.1s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'var(--mantine-color-gray-1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                <Group gap={6} wrap="nowrap">
                                  <Icon size={14} color={`var(--mantine-color-${primitive.phaseColor}-6)`} />
                                  <Text size="xs" fw={500} style={{ flex: 1 }}>
                                    {primitive.name}
                                  </Text>
                                  {/* OPTIONAL: Show live count
                                  {count > 0 && (
                                    <Badge size="xs" variant="light" c={primitive.phaseColor}>
                                      {count}
                                    </Badge>
                                  )}
                                  */}
                                </Group>
                              </UnstyledButton>
                            );
                          })}
                        </Stack>
                        {phase !== 'OPTIMIZE' && <Divider my="sm" />}
                      </div>
                    ))}
                  </Stack>
                </ScrollArea>
              </Collapse>

              {/* Toggle Button */}
              <Box
                p="xs"
                style={{
                  background: 'var(--mantine-color-violet-1)',
                  borderLeft: opened ? '1px solid var(--mantine-color-gray-3)' : 'none',
                  borderTopLeftRadius: opened ? 0 : 'var(--mantine-radius-md)',
                  borderBottomLeftRadius: opened ? 0 : 'var(--mantine-radius-md)',
                  cursor: 'pointer',
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                }}
                onClick={() => setOpened(!opened)}
              >
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon variant="subtle" size="sm">
                    {opened ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
                  </ActionIcon>
                  <Text size="sm" fw={600} c="violet">
                    Workflow Orchestration (Ctrl+O)
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    c="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </Group>
              </Box>
            </Group>
          )}
        </Paper>
      )}
    </Transition>
  );
}

export default FloatingOrchestrationMenu;
